import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import MenuItem from '@material-ui/core/MenuItem'
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress'
import { unwrapResult } from '@reduxjs/toolkit'
import { format as dateFormat } from 'date-fns'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import FormikTextField from 'components/Fields/FormikTextField'
import { apiDateFormat } from 'shared/constants/AppConst'
import { OfferingTypes } from 'shared/constants/AppEnum'
import { useAppDispatch } from 'store/hooks'
import { offeringUpdateThunk } from 'store/reducers/temple/offeringSlice'
import { IOfferingResponse } from 'types/api/temple'
import { IOfferingMetaType, WeeklyDaysFlagType } from 'types/store/temple'
import { capStr } from 'utils/fns'
import { extractError } from 'utils/api'
import WeeklyDays from '../Bookable/WeeklyDays'
import FormBlock from 'components/Form/FormBlock'
import MultiDateCalendar from './MultiDateCalendar'

type BulkOfferingTypeDatesForm = {
    offeringType: OfferingTypes
    customDates: Date[]
    excludedDates: Date[]
    weekDays: WeeklyDaysFlagType
}

interface BulkOfferingTypeDatesDialogProps {
    open: boolean
    offerings: IOfferingResponse[]
    onClose: () => void
    onComplete?: () => void
}

const emptyWeekDays = (): WeeklyDaysFlagType => ({
    sun: false,
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false
})

const initialValues = (): BulkOfferingTypeDatesForm => ({
    offeringType: OfferingTypes.DAILY,
    customDates: [],
    excludedDates: [],
    weekDays: emptyWeekDays()
})

const supportedOfferingTypes = [OfferingTypes.CUSTOM, OfferingTypes.WEEKLY, OfferingTypes.DAILY]

type FailedUpdate = {
    offering: IOfferingResponse
    error: unknown
}

type BulkProgress = {
    total: number
    completed: number
    succeeded: IOfferingResponse[]
    failed: FailedUpdate[]
}

const uniqueSorted = <T extends string | number>(values: T[]): T[] =>
    Array.from(new Set(values)).sort()

const getOfferingMeta = (offering: IOfferingResponse, values: BulkOfferingTypeDatesForm): IOfferingMetaType => {
    const dates = values.customDates.map(date => dateFormat(date, apiDateFormat))
    const excludes = values.excludedDates.map(date => dateFormat(date, apiDateFormat))
    const days = Object.entries(values.weekDays)
        .filter(([, selected]) => selected)
        .map(([day]) => day as keyof WeeklyDaysFlagType)

    if (values.offeringType === OfferingTypes.CUSTOM) {
        const existingDates = offering.offeringType === OfferingTypes.CUSTOM
            ? offering.offeringMeta?.dates ?? []
            : []
        return { dates: uniqueSorted([...existingDates, ...dates]) }
    }
    if (values.offeringType === OfferingTypes.WEEKLY) {
        const existingExcludes = offering.offeringType !== OfferingTypes.CUSTOM
            ? offering.offeringMeta?.excludes ?? []
            : []
        const existingDays = offering.offeringType === OfferingTypes.WEEKLY
            ? offering.offeringMeta?.days ?? []
            : []
        return {
            days: uniqueSorted([...existingDays, ...days]),
            excludes: uniqueSorted([...existingExcludes, ...excludes])
        }
    }
    const existingExcludes = offering.offeringType !== OfferingTypes.CUSTOM
        ? offering.offeringMeta?.excludes ?? []
        : []
    return { excludes: uniqueSorted([...existingExcludes, ...excludes]) }
}

const BulkOfferingTypeDatesDialog: React.FC<BulkOfferingTypeDatesDialogProps> = ({ open, offerings, onClose, onComplete }) => {
    const dispatch = useAppDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { messages } = useIntl()
    const [progress, setProgress] = useState<BulkProgress | null>(null)
    const isRunning = Boolean(progress && progress.completed < progress.total)

    useEffect(() => {
        if (!open) setProgress(null)
    }, [open])

    const applyUpdates = async (values: BulkOfferingTypeDatesForm, targets: IOfferingResponse[]) => {
        const nextProgress: BulkProgress = { total: targets.length, completed: 0, succeeded: [], failed: [] }
        setProgress(nextProgress)

        const results = await Promise.all(targets.map(async offering => {
            try {
                await dispatch(offeringUpdateThunk({
                    url: offering.url,
                    data: {
                        offeringType: values.offeringType,
                        offeringMeta: getOfferingMeta(offering, values)
                    }
                })).then(unwrapResult)
                setProgress(previous => previous ? {
                    ...previous,
                    completed: previous.completed + 1,
                    succeeded: [...previous.succeeded, offering]
                } : previous)
                return { offering, success: true as const }
            } catch (error) {
                setProgress(previous => previous ? {
                    ...previous,
                    completed: previous.completed + 1,
                    failed: [...previous.failed, { offering, error }]
                } : previous)
                return { offering, success: false as const, error }
            }
        }))

        const succeeded = results.filter(result => result.success).length
        const failed = results.length - succeeded
        if (failed === 0) {
            enqueueSnackbar(messages['offering.bulkTypeDates.success'] as string, { variant: 'success' })
            onComplete?.()
            onClose()
        } else if (succeeded > 0) {
            enqueueSnackbar(`${succeeded} offering${succeeded === 1 ? '' : 's'} updated; ${failed} failed`, { variant: 'warning' })
        } else {
            enqueueSnackbar(messages['offering.bulkTypeDates.failure'] as string, { variant: 'error' })
        }
    }

    return (
        <Dialog open={open} onClose={isRunning ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{messages['offering.bulkTypeDates.title']}</DialogTitle>
            <Formik
                key={open ? 'open' : 'closed'}
                initialValues={initialValues()}
                onSubmit={async (values, { setSubmitting }) => {
                    await applyUpdates(values, offerings)
                    setSubmitting(false)
                }}
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form>
                        <DialogContent>
                            <Box mb={2}>
                                <Typography variant="body2" color="textSecondary">
                                    Apply the type and dates to {offerings.length} selected offering{offerings.length === 1 ? '' : 's'}.
                                </Typography>
                            </Box>
                            <FormikTextField
                                select
                                name="offeringType"
                                label={messages['offering.offeringType']}
                                variant="outlined"
                                fullWidth
                                disabled={isSubmitting}
                            >
                                {supportedOfferingTypes.map(type => (
                                    <MenuItem key={type} value={type}>{capStr(type)}</MenuItem>
                                ))}
                            </FormikTextField>
                            <Box mt={3}>
                                {values.offeringType === OfferingTypes.CUSTOM ? (
                                    <FormBlock titleId="offering.customOffering.action.title">
                                        <MultiDateCalendar
                                            dates={values.customDates}
                                            onChange={dates => setFieldValue('customDates', dates)}
                                            disabled={isSubmitting}
                                        />
                                    </FormBlock>
                                ) : (
                                    <>
                                        {values.offeringType === OfferingTypes.WEEKLY && (
                                            <WeeklyDays
                                                formContext="weekDays"
                                                formState={values.weekDays}
                                                setFieldValue={setFieldValue}
                                                disabled={isSubmitting}
                                            />
                                        )}
                                        <FormBlock titleId="offering.excluded">
                                            <MultiDateCalendar
                                                dates={values.excludedDates}
                                                onChange={dates => setFieldValue('excludedDates', dates)}
                                                disabled={isSubmitting}
                                            />
                                        </FormBlock>
                                    </>
                                )}
                            </Box>
                            {progress && (
                                <Box mt={3}>
                                    <Typography variant="body2">
                                        {progress.completed < progress.total
                                            ? `${messages['offering.bulkTypeDates.updating']} ${progress.completed} ${messages['offering.bulkTypeDates.of']} ${progress.total}`
                                            : `${progress.succeeded.length} ${messages['offering.bulkTypeDates.succeeded']}; ${progress.failed.length} ${messages['offering.bulkTypeDates.failed']}`}
                                    </Typography>
                                    <Box mt={1}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress.total ? (progress.completed / progress.total) * 100 : 0}
                                        />
                                    </Box>
                                    {progress.failed.length > 0 && !isRunning && (
                                        <Box mt={2}>
                                            {progress.failed.map(({ offering, error }) => (
                                                <Typography key={offering.id} variant="body2" color="error">
                                                    {offering.name}: {extractError(error)}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="primary" variant="outlined" disabled={isSubmitting}>
                                {messages['common.cancel']}
                            </Button>
                            <Button color="primary" variant="contained" type="submit" disabled={isSubmitting || offerings.length === 0}>
                                {messages['common.apply'] ?? 'Apply'}
                            </Button>
                            {progress && progress.failed.length > 0 && !isRunning && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    disabled={isSubmitting}
                                    onClick={async () => {
                                        setSubmitting(true)
                                        await applyUpdates(values, progress.failed.map(item => item.offering))
                                        setSubmitting(false)
                                    }}
                                >
                                    {messages['offering.bulkTypeDates.retryFailed']}
                                </Button>
                            )}
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    )
}

export default BulkOfferingTypeDatesDialog
